"use client";

import { useEffect, useState } from "react";
import Link from "next/link";
import axios from "axios";
import type { LaptopInformation, LaptopScores, RecommendationsResponse } from "@/lib/types";
import { LaptopInformationDetail } from "@/components/laptop-information-detail";
import { RecommendationQuestionnaire } from "@/components/recommendation-questionnaire";
import { buildRecommendationQuery } from "@/lib/algorithm";
import { SCALERS } from "@/lib/scalers";
import type { Answer, Reference } from "@/lib/match-explanations";

const API_BASE = process.env.NEXT_PUBLIC_API_BASE_URL ?? "https://laptop-search-tool-dev.onrender.com";

// The interactive half of a /spec page.
//
// The page itself stays a Server Component so ISR and the crawlable HTML are
// untouched. This island renders exactly the markup that page used to render —
// the Q&A banner plus the laptop detail — until the visitor opts into the Q&A,
// so the prerendered HTML a crawler sees is unchanged.

type Props = {
  item: LaptopInformation;
  country: string;
  series: string;
  config: string;
};

type Phase = "cta" | "qa" | "loading" | "scored";

type ScoreResponse = {
  rank: number | null;
  match_score: number | null;
  scores: LaptopScores;
};

function Banner({ onStart }: { onStart: () => void }) {
  return (
    <div className="border-b border-border/60 bg-brand/5">
      <div className="mx-auto flex max-w-7xl flex-col items-start gap-3 px-5 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-8">
        <p className="text-sm text-muted-foreground">
          Is this the right laptop for <em>you</em>? Answer a few quick questions and we&apos;ll score
          this one against how you&apos;ll actually use it — then you can download the report.
        </p>
        <button
          onClick={onStart}
          className="inline-flex h-10 shrink-0 items-center justify-center rounded-full bg-brand px-5 text-sm font-semibold text-brand-foreground shadow-sm ring-1 ring-brand transition-all hover:opacity-95 active:scale-95"
        >
          Score this laptop →
        </button>
      </div>
    </div>
  );
}

export function SpecInteractive({ item, country, series, config }: Props) {
  const [phase, setPhase] = useState<Phase>("cta");
  const [scores, setScores] = useState<LaptopScores | null>(null);
  const [reference, setReference] = useState<Reference>({});
  const [answers, setAnswers] = useState<Record<string, Answer>>({});
  const [allItems, setAllItems] = useState<LaptopInformation[]>([]);
  const [hasMoreRecs, setHasMoreRecs] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Read in an effect, never during render: sessionStorage does not exist on the
  // server, and touching it during render would desync the prerendered HTML.
  const [savedAnswers, setSavedAnswers] = useState<Record<string, Answer> | null>(null);
  useEffect(() => {
    try {
      const raw = sessionStorage.getItem("recommendation-answers");
      if (raw) setSavedAnswers(JSON.parse(raw) as Record<string, Answer>);
    } catch {
      // Corrupt storage is not worth failing over — just ask the questions again.
    }
  }, []);

  // Answering the questions scrolls the page well down, so without this the
  // score, and the download button under it, land off-screen above the user.
  useEffect(() => {
    if (phase === "scored") {
      window.scrollTo({ top: 0, behavior: "instant" as ScrollBehavior });
    }
  }, [phase]);

  const submit = async (given: Record<string, Answer>) => {
    // The page's country wins over anything the questionnaire holds: we are
    // scoring this laptop on this country's route.
    const merged = { ...given, country };
    const { features, budget, brands, allowRefurbished, requireTouchscreen } =
      buildRecommendationQuery(merged);

    setPhase("loading");
    setError(null);

    const params = new URLSearchParams();
    params.set("country", country);
    if (budget !== undefined) params.set("max_price", String(budget));
    if (allowRefurbished) params.set("allow_refurbished", "true");
    if (requireTouchscreen) params.set("require_touchscreen", "true");
    brands.forEach((b) => params.append("brands", b));

    const body = { reference: features, scalers: SCALERS };
    // The score endpoint re-anchors its CPU/GPU targets against the in-budget pool,
    // and refurbished listings change what is in budget — so it has to be told too,
    // or this laptop's /spec score would drift from its score in the shortlist.
    const scoreUrl =
      `${API_BASE}/recommend/laptop/${encodeURIComponent(country)}/${encodeURIComponent(series)}/${encodeURIComponent(config)}/score` +
      `?max_price=${encodeURIComponent(String(budget ?? 0))}` +
      (allowRefurbished ? "&allow_refurbished=true" : "");

    // allSettled, not all: /recommend/rec 404s when nothing is in budget, while
    // the score endpoint happily scores an out-of-budget laptop. A visitor whose
    // budget rules everything out should still get this laptop's score and their
    // PDF — they just don't get a "see more recommendations" link.
    const [scoreRes, recRes] = await Promise.allSettled([
      axios.post<ScoreResponse>(scoreUrl, body),
      axios.post<RecommendationsResponse>(`${API_BASE}/recommend/rec?${params.toString()}`, body),
    ]);

    if (scoreRes.status !== "fulfilled") {
      setError("We couldn't score this laptop just now. Please try again.");
      setPhase("qa");
      return;
    }

    setScores(scoreRes.value.data.scores);
    setReference(features as Reference);
    setAnswers(merged);

    try {
      sessionStorage.setItem("recommendation-answers", JSON.stringify(merged));
      sessionStorage.setItem("recommendation-brands", JSON.stringify(brands));
      sessionStorage.setItem(
        "recommendation-request",
        JSON.stringify({
          country,
          max_price: budget,
          brands,
          allow_refurbished: allowRefurbished,
          reference: features,
          scalers: SCALERS,
        }),
      );

      if (recRes.status === "fulfilled") {
        sessionStorage.setItem("recommendations", JSON.stringify(recRes.value.data));
        setAllItems(recRes.value.data.laptops ?? []);
        setHasMoreRecs((recRes.value.data.laptops?.length ?? 0) > 0);
      }
    } catch {
      // Private-mode / full storage: the score and the PDF still work, we just
      // can't hand the answers on to the recommendations page.
    }

    setPhase("scored");
  };

  if (phase === "qa" || phase === "loading") {
    return (
      <div className="relative">
        {error && (
          <div className="mx-auto max-w-3xl px-5 pt-4 sm:px-8">
            <p className="rounded-xl bg-destructive/10 px-4 py-3 text-sm font-medium text-destructive">
              {error}
            </p>
          </div>
        )}

        <RecommendationQuestionnaire
          lockedCountry={country}
          initialAnswers={savedAnswers ?? undefined}
          onComplete={submit}
          submitLabel="Score this laptop"
          finalCtaLabel="Score this laptop →"
        />

        {phase === "loading" && (
          <div className="fixed inset-0 z-[100] flex flex-col items-center justify-center bg-background/95">
            <p className="text-lg font-semibold tracking-tight text-foreground">
              Scoring this laptop
            </p>
            <p className="mt-1 text-sm text-muted-foreground">Comparing it against your answers…</p>
          </div>
        )}
      </div>
    );
  }

  if (phase === "scored" && scores) {
    return (
      <>
        {/* Takes the slot the "Score this laptop" banner occupied, so the answer
            to "what now?" sits where the question used to be. */}
        {hasMoreRecs && (
          <div className="border-b border-border/60 bg-brand/5">
            <div className="mx-auto flex max-w-7xl flex-col items-start gap-3 px-5 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-8">
              <p className="text-sm text-muted-foreground">
                We also matched you with other laptops based on the same answers.
              </p>
              <Link
                href="/recommendations"
                className="inline-flex h-10 shrink-0 items-center justify-center rounded-full bg-brand px-5 text-sm font-semibold text-brand-foreground shadow-sm ring-1 ring-brand transition-all hover:opacity-95 active:scale-95"
              >
                See more recommendations →
              </Link>
            </div>
          </div>
        )}

        <LaptopInformationDetail
          item={item}
          matchScores={scores}
          reference={reference}
          answers={answers}
          country={country}
          series={series}
          config={config}
          allItems={allItems}
        />
      </>
    );
  }

  // phase === "cta" — the default, and what gets prerendered.
  return (
    <>
      <Banner onStart={() => setPhase("qa")} />
      <LaptopInformationDetail item={item} country={country} series={series} config={config} />
    </>
  );
}
